import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Briefcase, Search, Filter, ChevronRight, AlertCircle } from 'lucide-react'
import api from '../config/api'
import { formatDate } from '../utils/formatDate'
import '../styles/MyMatters.css'

function MyMatters() {
  const [matters, setMatters] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchMatters()
  }, [filter])

  const fetchMatters = async () => {
    try {
      setLoading(true)
      const params = filter !== 'all' ? { status: filter } : {}
      const response = await api.get('/matters', { params })
      setMatters(response.data.data)
    } catch (err) {
      setError('Failed to load matters')
    } finally {
      setLoading(false)
    }
  }

  const filteredMatters = matters.filter(matter =>
    matter.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    matter.matterNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const statusColors = {
    open: 'status-open',
    active: 'status-active',
    pending: 'status-pending',
    closed: 'status-closed'
  }

  return (
    <div className="my-matters-page">
      <header className="page-header">
        <div className="header-content">
          <Briefcase size={28} />
          <div>
            <h1>My Matters</h1>
            <p>View and track your legal matters</p>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="matters-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search matters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-pills">
          {['all', 'open', 'active', 'pending', 'closed'].map((status) => (
            <button
              key={status}
              className={`filter-pill ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading matters...</p>
        </div>
      ) : filteredMatters.length === 0 ? (
        <div className="empty-state">
          <Briefcase size={48} />
          <h3>No matters found</h3>
          <p>
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'You don\'t have any matters yet'}
          </p>
        </div>
      ) : (
        <div className="matters-list">
          {filteredMatters.map((matter, index) => (
            <motion.div
              key={matter.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={`/matters/${matter.id}`} className="matter-card">
                <div className="matter-info">
                  <div className="matter-header">
                    <span className="matter-number">{matter.matterNumber}</span>
                    <span className={`matter-status ${statusColors[matter.status] || ''}`}>
                      {matter.status}
                    </span>
                  </div>
                  <h3 className="matter-title">{matter.title}</h3>
                  <div className="matter-meta">
                    <span className="matter-type">{matter.type}</span>
                    {matter.openDate && (
                      <span className="matter-date">
                        Opened: {formatDate(matter.openDate)}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={20} className="matter-arrow" />
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MyMatters
